/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { FC, lazy, useEffect } from 'react';
import { Routes, Route, BrowserRouter, Navigate, useSearchParams } from 'react-router-dom';
import { PrivateRoutes } from './PrivateRoutes';
import { ErrorsPage } from '../modules/errors/ErrorsPage';
import { Logout, AuthPage, useAuth } from '../modules/auth';
import { App } from '../App';
import { GAListener } from './GAListner';
import { baseDomain, nameFromDomain } from '../helpers/funcs';
import { Helmet } from 'react-helmet';
const Onboarding = lazy(() => import('../modules/apps/clients/OnboardingPage'));
const Intake = lazy(() => import('../modules/apps/clients/IntakePage'));
const Reschedule = lazy(() => import('../modules/apps/clients/ReschedulePage'));
const PaymentResolution = lazy(() => import('../modules/apps/clients/PaymentResolutionPage'));

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env;

const AppRoutes: FC = () => {
  const { currentUser } = useAuth();
  const url = new URL(window.location.href);
  const queryString = url.search;
  const currentPath = window.location.pathname;
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Helmet>
        <title>{nameFromDomain || 'Wiz'}</title>
      </Helmet>
      <GAListener>
        <Routes>
          <Route element={<App />}>
            <Route path="error/*" element={<ErrorsPage />} />
            <Route path="logout" element={<Logout />} />
            {/* WST-654 — anonymous, token-authenticated; available regardless of session */}
            <Route path="p/:token/" element={<PaymentResolution />} />
            <Route path="p/:token" element={<PaymentResolution />} />
            {currentUser ? (
              <>
                <Route path="/*" element={<PrivateRoutes />} />
                <Route index element={<Navigate to="/home" />} />
              </>
            ) : (
              <>
                <Route path="reschedule/:token/" element={<Reschedule />} />
                <Route path="m/:token/" element={<Reschedule />} />
                <Route path="i/:inviteId" element={<Onboarding />} />
                <Route path="auth/*" element={<AuthPage />} />
                <Route path="account-setup" element={<Navigate to={`/auth/account-setup/${queryString}`} />} />
                <Route path="/:formId/:inviteId" element={<Intake />} />
                <Route path="/:formId/:inviteId/" element={<Intake />} />
                <Route path="/:formId/" element={<Intake />} />
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={
                        queryString
                          ? `/auth${queryString}`
                          : currentPath && currentPath !== '/'
                          ? `/auth?next=${currentPath}`
                          : '/auth'
                      }
                    />
                  }
                />
              </>
            )}
          </Route>
        </Routes>
      </GAListener>
    </BrowserRouter>
  );
};

export { AppRoutes };
